import log from 'loglevel';
import type { Logger } from 'loglevel';
import type { Map } from 'mapbox-gl';

import BaseService from './BaseService';

/** Esperas de los reintentos, en milisegundos. */
interface RetrySettings {
    /** Antes del primer reintento. */
    delay: number;
    /** Tope de la espera, que se dobla en cada reintento. */
    maxDelay: number;
    /** Sin errores durante este tiempo, la siguiente racha vuelve a delay. */
    reset: number;
}

/**
 * Evento error de Mapbox. Si el fallo viene de una fuente, Mapbox añade
 * sourceId y, si además es de una tesela, la tesela; @types/mapbox-gl solo
 * declara error.
 */
interface MapErrorEvent {
    error?: { message?: string };
    sourceId?: string;
    tile?: { tileID: { canonical: { z: number; x: number; y: number } } };
}

/** Reintentos de una fuente o del estilo durante una racha de errores. */
interface Retry {
    /** Reintentos hechos en esta racha. */
    attempts: number;
    /** Momento del último error, para saber cuándo acaba la racha. */
    lastErrorAt: number;
    /** Reintento programado, si hay uno pendiente. */
    timer?: ReturnType<typeof setTimeout>;
    /** Motivo por el que el reintento está pospuesto, si lo está. */
    waitingFor?: string;
}

/**
 * Estado de la app que decide si tiene sentido reintentar. Llega desde fuera
 * para no importar NetworkService ni AppLifecycleService: los dos están en el
 * ciclo de imports de common, y este servicio entraría en él.
 */
export interface MapAppState {
    /** Si la app está sin red. */
    isOffline: () => boolean;
    /** Si la app está en segundo plano y nadie ve el mapa. */
    isInBackground: () => boolean;
}

/**
 * Detecta los fallos de carga del mapa y los recupera (#507).
 *
 * Mapbox no reintenta nada por su cuenta y no pone timeout a sus peticiones.
 * El evento error es su único aviso:
 *
 * - Si falla una tesela o un TileJSON, Mapbox lo da por terminado y no lo
 *   vuelve a pedir mientras siga a la vista: el mapa base se queda vacío
 *   aunque vuelva la red. Se fuerza un repintado, porque un fallo de red no lo
 *   pide por sí solo (lo mismo que maplibre-gl-js#8716) y sin él load e idle
 *   pueden no llegar nunca, y se recarga la fuente con reload(), que vuelve a
 *   pedir el TileJSON y descarta todas sus teselas.
 * - Si falla o se cuelga la descarga del estilo, el mapa se queda en blanco
 *   sin llegar al load. Se carga de nuevo con setStyle().
 *
 * Los reintentos esperan cada vez el doble mientras sigan llegando errores.
 * Sin red o con la app en segundo plano se posponen: fallarían seguro, o nadie
 * vería el mapa.
 */
class MapErrorService extends BaseService {

    /** Reintentos en curso de las fuentes, por sourceId. */
    static retries: Record<string, Retry> = {};

    /** Reintentos en curso del estilo. */
    static styleRetry: Retry | undefined;

    /** Si el estilo ya ha cargado: a partir de ahí no se vuelve a cargar. */
    static styleLoaded = false;

    /** Plazo de la descarga del estilo en curso. */
    static styleTimeout?: ReturnType<typeof setTimeout>;

    /** Estado de la app que decide si se reintenta. @see watch */
    static appState: MapAppState = { isOffline: () => false, isInBackground: () => false };

    static logger(): Logger {
        return log.getLogger('MapErrorService');
    }

    /**
     * Esperas de los reintentos, a partir de la configuración en segundos:
     *
     * - map_reload_delay_seconds: antes del primer reintento.
     * - map_reload_max_delay_seconds: tope de la espera, que se dobla en cada
     *   reintento.
     * - map_reload_reset_seconds: tras este tiempo sin errores, el siguiente
     *   fallo empieza una racha nueva y vuelve a la primera espera. Tiene que
     *   superar el tope: si no, una caída larga, con reintentos al tope que
     *   vuelven a fallar, reiniciaría la espera en cada intento.
     */
    static retrySettings(): RetrySettings {
        const config = this.config();
        return {
            delay: config.map_reload_delay_seconds * 1000,
            maxDelay: config.map_reload_max_delay_seconds * 1000,
            reset: config.map_reload_reset_seconds * 1000,
        };
    }

    /**
     * Escucha el mapa desde su creación: los fallos de carga llegan antes que
     * el evento load.
     *
     * Con un listener de error Mapbox deja de escribirlos en consola, así que
     * los que no son de una fuente se registran aquí tal cual.
     *
     * @param appState Si la app está sin red o en segundo plano.
     */
    static watch(map: Map, appState: MapAppState): void {
        this.appState = appState;
        const { maxDelay, reset } = this.retrySettings();
        if (reset <= maxDelay) {
            this.logger().warn('map_reload_reset_seconds debería ser mayor que map_reload_max_delay_seconds: en una caída larga, la espera volverá a empezar en cada recarga');
        }

        this.styleLoaded = false;
        map.on('style.load', () => {
            this.styleLoaded = true;
            clearTimeout(this.styleTimeout);
            if (this.styleRetry) {
                this.logger().info(`Estilo cargado tras ${this.styleRetry.attempts} recargas`);
            }
        });
        this.armStyleTimeout(map);

        map.on('error', (event) => {
            this.handleError(map, event as MapErrorEvent);
        });

        map.on('remove', () => {
            this.reset();
        });
    }

    /**
     * Cancela los reintentos y el plazo del estilo pendientes, y deja el estado
     * como al principio. Se llama al destruir el mapa (al cerrar sesión en la
     * web): si no, los temporizadores actuarían sobre el mapa eliminado, y un
     * reintento pendiente de una fuente impediría programar el del mapa nuevo
     * para esa misma fuente.
     */
    static reset(): void {
        Object.values(this.retries).forEach((retry) => clearTimeout(retry.timer));
        clearTimeout(this.styleRetry?.timer);
        clearTimeout(this.styleTimeout);
        this.retries = {};
        this.styleRetry = undefined;
        this.styleTimeout = undefined;
        this.styleLoaded = false;
    }

    static handleError(map: Map, event: MapErrorEvent): void {
        if (!event.sourceId) {
            this.logger().error('Error del mapa:', event.error);
            // Antes de style.load, el único error sin fuente es la descarga
            // del estilo. Después vienen el sprite o las fuentes tipográficas,
            // que no impiden cargar el mapa.
            if (!this.styleLoaded) {
                this.scheduleStyleReload(map);
            }
            return;
        }

        const message = event.error?.message ?? String(event.error);
        if (event.tile) {
            const { z, x, y } = event.tile.tileID.canonical;
            this.logger().warn(`Fallo detectado en ${event.sourceId}: tesela ${z}/${x}/${y} (${message})`);
        } else {
            this.logger().warn(`Fallo detectado en ${event.sourceId}: TileJSON (${message})`);
        }

        map.triggerRepaint();
        this.scheduleReload(map, event.sourceId);
    }

    /**
     * Una descarga colgada no da error: si el estilo no ha cargado en
     * map_style_timeout_seconds, se trata como un fallo. Se rearma en cada
     * recarga; con 0 no hay plazo.
     */
    static armStyleTimeout(map: Map): void {
        const seconds = this.config().map_style_timeout_seconds;
        clearTimeout(this.styleTimeout);
        if (!seconds) {
            return;
        }

        this.styleTimeout = setTimeout(() => {
            if (this.styleLoaded) {
                return;
            }
            this.logger().warn(`El estilo no ha cargado en ${seconds} s`);
            this.scheduleStyleReload(map);
        }, seconds * 1000);
    }

    static scheduleReload(map: Map, sourceId: string): void {
        const retry = this.retries[sourceId] = this.continueStreak(this.retries[sourceId]);
        this.scheduleRetry(retry, `Recarga de ${sourceId}`, (attempt) => {
            this.reloadSource(map, sourceId, attempt);
        });
    }

    static scheduleStyleReload(map: Map): void {
        const retry = this.styleRetry = this.continueStreak(this.styleRetry);
        this.scheduleRetry(retry, 'Recarga del estilo', (attempt) => {
            this.reloadStyle(map, attempt);
        });
    }

    /**
     * Anota un error en la racha en curso, o empieza una nueva si el último
     * error fue hace más de map_reload_reset_seconds. Con un reintento
     * pendiente la racha sigue: sin red puede pasar mucho tiempo sin errores.
     */
    static continueStreak(retry: Retry | undefined): Retry {
        const now = Date.now();
        if (!retry || (!retry.timer && now - retry.lastErrorAt > this.retrySettings().reset)) {
            retry = { attempts: 0, lastErrorAt: now };
        }
        retry.lastErrorAt = now;
        return retry;
    }

    /**
     * Programa un reintento, salvo que ya haya uno pendiente: los errores de
     * una misma carga llegan seguidos y basta con un reintento.
     *
     * @param description Inicio del mensaje de log, como "Recarga del estilo".
     */
    static scheduleRetry(retry: Retry, description: string, action: (attempt: number) => void): void {
        if (retry.timer) {
            return;
        }

        const { delay: firstDelay, maxDelay } = this.retrySettings();
        const delay = Math.min(firstDelay * 2 ** retry.attempts, maxDelay);
        this.logger().info(`${description} en ${delay / 1000} s`);
        this.armRetry(retry, delay, description, action);
    }

    /**
     * Arma el temporizador del reintento. Si al cumplirse no tiene sentido
     * reintentar (@see waitReason), se pospone con la misma espera, sin contar
     * como intento.
     */
    static armRetry(retry: Retry, delay: number, description: string, action: (attempt: number) => void): void {
        retry.timer = setTimeout(() => {
            retry.timer = undefined;
            const reason = this.waitReason();
            if (reason) {
                if (retry.waitingFor !== reason) {
                    retry.waitingFor = reason;
                    this.logger().info(`${description} pospuesta ${reason}`);
                }
                this.armRetry(retry, delay, description, action);
                return;
            }

            retry.waitingFor = undefined;
            retry.attempts++;
            action(retry.attempts);
        }, delay);
    }

    /**
     * Por qué no tiene sentido reintentar ahora, o undefined si lo tiene: sin
     * red el reintento fallaría seguro, y en segundo plano nadie ve el mapa.
     */
    static waitReason(): string | undefined {
        if (this.appState.isOffline()) {
            return 'hasta que vuelva la red';
        }
        if (this.appState.isInBackground()) {
            return 'mientras la app está en segundo plano';
        }
        return undefined;
    }

    static reloadSource(map: Map, sourceId: string, attempt: number): void {
        const source = map.getSource(sourceId);
        // Solo se recargan las fuentes de teselas; las GeoJSON de la app no se
        // piden por red.
        if (!source || !('reload' in source)) {
            return;
        }

        this.logger().info(`Recargando ${sourceId} (intento ${attempt})`);
        source.reload();
    }

    /**
     * Carga el estilo desde cero. Solo antes de que haya cargado: después
     * borraría las capas y fuentes que añade la app. setStyle cancela la
     * descarga anterior si sigue colgada.
     */
    static reloadStyle(map: Map, attempt: number): void {
        if (this.styleLoaded) {
            return;
        }

        this.logger().info(`Recargando el estilo (intento ${attempt})`);
        map.setStyle(this.config().mapbox_style, { diff: false });
        this.armStyleTimeout(map);
    }
}

export default MapErrorService;
